import {ReactNode, useCallback, useEffect, useState} from 'react';
import {useTasteGenreSelectDialog} from '../../../appConfig/useTasteGenreSelectDialog';
import {ColorMode} from '../../../colorMode/ColorMode.enum';
import {IsLoginChecker} from '../../../common/checkers/IsLoginChecker';
import {setIsDevBlockHidden} from '../../../common/DeveloperSlice';
import {t} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import {useColorMode} from '../../../common/useColorMode';
import useTypedDispatch from '../../../common/useTypedDispatch';
import useTypedSelector from '../../../common/useTypedSelector';
import {useDialog} from '../../../dialog/useDialog.hook';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {useDeviceInfoQuery} from '../../../wrapper/deviceWrapper';
import {useTicketPopupOnDialog} from '../../buy/ticket/hook/useTicketPopupOnDialog';
import {DeviceInfoFetcherHeadless} from '../../device-management/headless/DeviceInfoFetcherHeadless';
import {SettingsProps} from '../presentational/Settings';

interface Props {
  children: (props: SettingsProps) => ReactNode;
}

export const SettingsContainer = ({children}: Props) => {
  const isMobile = requestContext.requestInfo.display.isMobile;
  const deviceInfo = useDeviceInfoQuery().data?.deviceInfo;
  const {storageColorMode, changeColorMode} = useColorMode();
  const [colorMode, setColorMode] = useState<ColorMode>();

  const dispatch = useTypedDispatch();
  const isDevBlockHidden = useTypedSelector(
    state => state.developer.isDevBlockHidden
  );

  const {DialogWrapper, Dialog, showDialog, hideDialog} = useDialog({
    isMobile,
  });

  const {
    showDialog: showTasteGenreDialog,
    tasteGenreSelectDialog,
    tastesGenre,
  } = useTasteGenreSelectDialog(isMobile);

  const {
    showDialog: showSelectTicketPopupOnDialog,
    ticketPopupOnDialog,
    ticketPopupOn,
  } = useTicketPopupOnDialog(isMobile);

  const handleClickColorMode = useCallback(
    (mode: ColorMode) => {
      changeColorMode(mode);
      setColorMode(mode);
      hideDialog();
    },
    [changeColorMode, hideDialog]
  );

  const handleToggleDevBlockButton = useCallback(() => {
    dispatch(setIsDevBlockHidden(!isDevBlockHidden));
  }, [dispatch, isDevBlockHidden]);

  useEffect(() => {
    setColorMode(storageColorMode);
  }, [storageColorMode]);

  return (
    <>
      <IsLoginChecker>
        <DeviceInfoFetcherHeadless>
          {children({
            isMobile,
            colorMode,
            ticketPopupOn,
            tastesGenre,
            deviceInfo,
            isDevBlockHidden,
            showDialog,
            showSelectTicketPopupOnDialog,
            showTasteGenreDialog,
            toggleDevBlockButton: handleToggleDevBlockButton,
          })}

          <DialogWrapper
            {...putTiaraObjectProps({
              click: {layer1: '설정'},
            })}
          >
            <Dialog.Dimmed />
            <Dialog.List className="space-y-0">
              <Dialog.Option
                isChecked={colorMode === ColorMode.Dark}
                onClick={e =>
                  LOG_CLICK_EVENT('화면스타일_클릭', e, () =>
                    handleClickColorMode(ColorMode.Dark)
                  )
                }
                {...putTiaraObjectProps({
                  click: {copy: '다크모드'},
                })}
              >
                {t('more_settings_screen_type_dark')}
              </Dialog.Option>
              <Dialog.Option
                isChecked={colorMode === ColorMode.Light}
                onClick={e =>
                  LOG_CLICK_EVENT('화면스타일_클릭', e, () =>
                    handleClickColorMode(ColorMode.Light)
                  )
                }
                {...putTiaraObjectProps({
                  click: {copy: '라이트모드'},
                })}
              >
                {t('more_settings_screen_type_light')}
              </Dialog.Option>
              <Dialog.Option
                isChecked={colorMode === ColorMode.System}
                onClick={e =>
                  LOG_CLICK_EVENT('화면스타일_클릭', e, () =>
                    handleClickColorMode(ColorMode.System)
                  )
                }
                {...putTiaraObjectProps({
                  click: {copy: '시스템설정사용'},
                })}
              >
                {t('more_settings_screen_type_system')}
              </Dialog.Option>
            </Dialog.List>
            <Dialog.BottomCombination>
              <Dialog.Button onClick={hideDialog}>
                {t('common_cancel_button')}
              </Dialog.Button>
            </Dialog.BottomCombination>
          </DialogWrapper>

          {ticketPopupOnDialog}
          {tasteGenreSelectDialog}
        </DeviceInfoFetcherHeadless>
      </IsLoginChecker>
    </>
  );
};
